import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useDashboard } from '@/hooks/useDashboard'
import { GeoCard } from './GeoCard'
import { AffiliateGeoCard } from './AffiliateGeoCard'
import { EverflowOffersTile } from './EverflowOffersTile'
import { GeneralDashboard } from './GeneralDashboard'
import { GiveUpFunnel } from './GiveUpFunnel'
import { GlobalOverflowBar } from './GlobalOverflowBar'
import { PublicOverflowSection } from './PublicOverflowSection'
import { PermanentGeoSection } from './PermanentGeoSection'
import { OverflowBanner } from './OverflowBanner'
import { OfferPausedBanner } from './OfferPausedBanner'
import { OfferResetTag } from './OfferResetTag'
import { Spinner } from '@/components/shared/Spinner'
import { Button } from '@/components/shared/Button'
import { useOverflowTimer } from '@/hooks/useOverflowTimer'

/**
 * ONE offer's caps: its geos, its overflow pool, its request tiles.
 *
 * Reached from the offer list (`/`), never rendered for every offer at once — the geo cards
 * below are the whole point of having selected an offer, and stacking several offers' worth of
 * them on one page is what made this view unreadable.
 */
export function DashboardPage() {
  const { user, offers, selectedOfferId, allocations, generalPools, tiers, everflowOffers, capUnit, overflowPools, overflowIsLocked, overflowWindow, publicOverflowGeos, permanentRequestGeos, priorityRank, priorityTotal, isLoading, error, refresh } =
    useDashboard()
  const [showGiveUpFunnel, setShowGiveUpFunnel] = useState(false)
  const { isActive: giveUpOpen, countdown } = useOverflowTimer()

  const hasOverflowCapacity = overflowPools.some((p) => p.has_capacity && !p.is_locked)
  const selectedOffer = offers.find((o) => o.id === selectedOfferId)
  const isPaused = !!selectedOffer?.is_paused

  if (isLoading && allocations.length === 0 && generalPools.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Spinner className="w-8 h-8" />
          <p className="text-sm text-text-muted">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  if (error && allocations.length === 0 && generalPools.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="w-12 h-12 rounded-full bg-brand-red/8 flex items-center justify-center">
            <span className="text-brand-red text-xl">!</span>
          </div>
          <p className="text-sm text-text-secondary">{error}</p>
          <Button variant="secondary" onClick={refresh}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  // General users get a different dashboard (shared pools, % only)
  if (user?.user_type === 'general' && generalPools.length > 0) {
    return <GeneralDashboard pools={generalPools} tiers={tiers} />
  }

  return (
    <div>
      {/* Offer header — the breadcrumb back to the list, plus which offer's day this is. */}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-xs text-text-muted transition-colors hover:text-text-primary"
          >
            ← All offers
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight text-text-primary">
              {selectedOffer?.name ?? 'Offer'}
            </h1>
            {selectedOffer && (
              <OfferResetTag offer={selectedOffer} serverNextReset={overflowWindow?.resets_at_utc} />
            )}
          </div>
        </div>
        {user?.user_type === 'affiliate' && (
          <div className="flex items-center gap-3">
            <span className="text-xs text-text-muted">
              {isPaused
                ? 'Offer is paused'
                : giveUpOpen
                  ? `Give-up open · resets in ${countdown.hours}h ${countdown.minutes}m`
                  : `Give-up opens in ${countdown.hours}h ${countdown.minutes}m`}
            </span>
            <Button variant="primary" disabled={!giveUpOpen || isPaused} onClick={() => setShowGiveUpFunnel(true)}>
              ↩ Give Up Cap
            </Button>
          </div>
        )}
      </div>
      {isPaused && <OfferPausedBanner offerName={selectedOffer?.name} />}
      {user?.user_type === 'affiliate' && (
        <EverflowOffersTile offers={everflowOffers} offerName={selectedOffer?.name} />
      )}
      {user?.user_type === 'affiliate' && (overflowPools.length > 0 || publicOverflowGeos.length > 0) && (
        <>
          <OverflowBanner
            overflowWindow={overflowWindow}
            overflowIsLocked={overflowIsLocked}
            priorityRank={priorityRank}
            priorityTotal={priorityTotal}
            hasOverflowCapacity={hasOverflowCapacity}
            offerSlug={selectedOffer?.slug}
          />
          {overflowPools.length > 0 && (
            <div className="mb-4">
              <GlobalOverflowBar pools={overflowPools} />
            </div>
          )}
          <PublicOverflowSection
            geos={publicOverflowGeos}
            overflowIsLocked={overflowIsLocked}
            offerSlug={selectedOffer?.slug}
            overflowWindow={overflowWindow}
            capUnit={capUnit}
            paused={isPaused}
          />
        </>
      )}
      {user?.user_type === 'affiliate' && permanentRequestGeos.length > 0 && (
        <PermanentGeoSection geos={permanentRequestGeos} capUnit={capUnit} paused={isPaused} />
      )}
      {allocations.length === 0 ? (
        <div className="py-12 text-center text-sm text-text-muted">
          No permanent caps for this offer yet.
          {permanentRequestGeos.length > 0 || publicOverflowGeos.length > 0
            ? ' Request access using the sections above.'
            : ''}
        </div>
      ) : (
        <>
          <h2 className="mb-2 text-sm font-semibold tracking-tight text-text-primary">
            Your geos &amp; caps
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {allocations.map(allocation =>
              user?.user_type === 'affiliate' ? (
                <AffiliateGeoCard
                  key={allocation.id}
                  allocation={allocation}
                  pool={overflowPools.find(p => p.geo_id === allocation.geo_id)}
                  capUnit={capUnit}
                  paused={isPaused}
                />
              ) : (
                <GeoCard key={allocation.id} allocation={allocation} paused={isPaused} />
              )
            )}
          </div>
        </>
      )}
      <GiveUpFunnel open={showGiveUpFunnel} onClose={() => setShowGiveUpFunnel(false)} />
    </div>
  )
}
